import React, { useState, useEffect, useCallback } from 'react';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import stateList from '../../../components/common/states';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const validateGstDetail = (gst, idx) => {
  const errors = [];
  if (!gst.gst_number.trim()) errors.push(`GST #${idx + 1}: GST Number is required`);
  if (!gst.state.trim()) errors.push(`GST #${idx + 1}: State is required`);
  if (!gst.state_code.trim()) errors.push(`GST #${idx + 1}: State Code is required`);
  if (!gst.address.trim()) errors.push(`GST #${idx + 1}: Address is required`);
  if (!gst.pincode.trim()) errors.push(`GST #${idx + 1}: Pincode is required`);
  if (!gst.pan_no.trim()) errors.push(`GST #${idx + 1}: PAN No is required`);
  if (!gst.cin_no.trim()) errors.push(`GST #${idx + 1}: CIN No is required`);
  if (!gst.email.trim()) errors.push(`GST #${idx + 1}: Email is required`);
  if (!gst.phone.trim()) errors.push(`GST #${idx + 1}: Phone is required`);
  return errors;
};

const validateForm = (form) => {
  const errors = {};
  let gstErrors = [];
  if (!form.gst_details || !Array.isArray(form.gst_details) || form.gst_details.length === 0) {
    gstErrors.push('At least one GST detail is required');
  } else {
    form.gst_details.forEach((gst, idx) => {
      gstErrors = gstErrors.concat(validateGstDetail(gst, idx));
    });
  }
  errors.gst_details = gstErrors;
  Object.keys(errors).forEach((key) => {
    if (!errors[key] || (Array.isArray(errors[key]) && errors[key].length === 0)) {
      delete errors[key];
    }
  });
  return errors;
};

const CompanyAddGstForm = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const [companyData, setCompanyData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [form, setForm] = useState({
    gst_details: [
      {
        gst_number: '',
        state: '',
        state_code: '',
        address: '',
        pincode: '',
        pan_no: '',
        cin_no: '',
        accountNo: '',
        ifscCode: '',
        bankName: '',
        bankAddress: '',
        accountHolderName: '',
        branch: '',
        bankstate: '',
        branchName: '',
        branchCode: '',
        email: '',
        phone: ''
      }
    ]
  });

  const fetchCompanyData = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/masters/company-get?page=1&limit=1000&search=`, {
        method: 'GET',
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch Company data');
      const result = await res.json();
      const company = result.data.find(item => item.id === parseInt(companyId));
      if (!company) {
        toast.error('Company not found');
        navigate('/company');
        return;
      }
      setCompanyData(company);
      setFetching(false);
    } catch (err) {
      toast.error('Failed to load Company data');
      navigate('/company');
    }
  }, [companyId, navigate]);

  useEffect(() => {
    fetchCompanyData();
  }, [fetchCompanyData]);

  const handleGstChange = (idx, e) => {
    if (e.target.name === 'state') {
      const selectedState = stateList.find(s => s.name === e.target.value);
      const newGstDetails = form.gst_details.map((gst, i) =>
        i === idx ? { ...gst, state: selectedState ? selectedState.name : '', state_code: selectedState ? selectedState.code : '' } : gst
      );
      setForm({ ...form, gst_details: newGstDetails });
    } else {
      const newGstDetails = form.gst_details.map((gst, i) =>
        i === idx ? { ...gst, [e.target.name]: e.target.value } : gst
      );
      setForm({ ...form, gst_details: newGstDetails });
    }
  };

  const handleGstBlur = (idx, e) => {
    const { name, value } = e.target;
    let error = '';
    if (name === 'gst_number' && !value.trim()) {
      error = `GST #${idx + 1}: GST Number is required`;
    }
    if (name === 'state' && !value.trim()) {
      error = `GST #${idx + 1}: State is required`;
    }
    if (name === 'address' && !value.trim()) {
      error = `GST #${idx + 1}: Address is required`;
    }
    if (name === 'pincode' && !value.trim()) {
      error = `GST #${idx + 1}: Pincode is required`;
    }
    if (name === 'pan_no' && !value.trim()) {
      error = `GST #${idx + 1}: PAN No is required`;
    }
    if (name === 'cin_no' && !value.trim()) {
      error = `GST #${idx + 1}: CIN No is required`;
    }
    if (name === 'email' && !value.trim()) {
      error = `GST #${idx + 1}: Email is required`;
    }
    if (name === 'phone' && !value.trim()) {
      error = `GST #${idx + 1}: Phone is required`;
    }
    if (error) {
      toast.error(error);
    }
  };

  const handleAddGst = () => {
    setForm({
      ...form,
      gst_details: [...form.gst_details, {
        gst_number: '',
        state: '',
        state_code: '',
        address: '',
        pincode: '',
        pan_no: '',
        cin_no: '',
        accountNo: '',
        ifscCode: '',
        bankName: '',
        bankAddress: '',
        accountHolderName: '',
        branch: '',
        bankstate: '',
        branchName: '',
        branchCode: '',
        email: '',
        phone: ''
      }]
    });
  };

  const handleRemoveGst = (idx) => {
    setForm({
      ...form,
      gst_details: form.gst_details.filter((_, i) => i !== idx)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const errors = validateForm(form);
    if (Object.keys(errors).length > 0) {
      Object.values(errors).forEach((msg) => {
        if (Array.isArray(msg)) {
          msg.forEach((m) => m && toast.error(m));
        } else if (msg) {
          toast.error(msg);
        }
      });
      setLoading(false);
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/api/masters/company-add-gst`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ company_details_id: companyId, gst_details: form.gst_details }),
        credentials: 'include',
      });
      const data = await res.json();
      if (res.ok) {
        toast.success('GST details added to Company successfully!');
        navigate('/company');
      } else {
        toast.error(data.error || 'Error occurred.');
      }
    } catch (err) {
      toast.error('Network error.');
    }
    setLoading(false);
  };

  if (fetching) {
    return (
      <div className="max-w-8xl mx-auto p-4 bg-white">
        <div className="text-center text-blue-600 py-6 text-[13px]">Loading Company data...</div>
      </div>
    );
  }

  if (!companyData) {
    return (
      <div className="max-w-8xl mx-auto p-4 bg-white">
        <div className="text-center text-red-600 py-6 text-[13px]">Company not found</div>
      </div>
    );
  }

  return (
    <div className="max-w-8xl mx-auto p-4 bg-white">
      <ToastContainer />
      <div className="flex justify-between items-center border-b pb-3 mb-5">
        <h2 className="text-[15px] font-medium text-gray-800">Add GST Details to :- {companyData.business_name}</h2>
        <button
          type="button"
          onClick={() => navigate('/company')}
          className="flex items-center gap-1 text-[#15803d] border border-[#15803d] px-3 py-1 text-[12px] rounded font-normal hover:bg-[#166534] hover:text-white hover:border-[#166534] transition"
        >
          <FaArrowLeft />
          Back to List
        </button>
      </div>

      

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* GST Details */}
        <div className="pt-2">
          <label className="block text-[13px] font-medium text-gray-800 mb-2">Add New GST Details</label>
          {form.gst_details.map((gst, idx) => (
            <div key={idx} className="border border-gray-300 p-4 mb-4 rounded">
              <div className="flex justify-between items-center mb-3">
                <h4 className="text-[13px] font-medium">GST Detail #{idx + 1}</h4>
                {form.gst_details.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveGst(idx)}
                    className="text-[12px] px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                  >
                    Remove
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[13px] text-gray-700 mb-1">GST Number</label>
                  <input
                    type="text"
                    name="gst_number"
                    value={gst.gst_number}
                    onChange={(e) => handleGstChange(idx, e)}
                    onBlur={(e) => handleGstBlur(idx, e)}
                    className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-blue-400 rounded-none"
                    required
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label className="block text-[13px] text-gray-700 mb-1">State</label>
                  <select
                    name="state"
                    value={gst.state}
                    onChange={(e) => handleGstChange(idx, e)}
                    onBlur={(e) => handleGstBlur(idx, e)}
                    className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-blue-400 rounded-none"
                    required
                  >
                    <option value="">Select State</option>
                    {stateList.map((s) => (
                      <option key={s.code} value={s.name}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[13px] text-gray-700 mb-1">State Code</label>
                  <input
                    type="text"
                    name="state_code"
                    value={gst.state_code}
                    readOnly
                    className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-gray-100 focus:outline-none rounded-none"
                  />
                </div>
                <div>
                  <label className="block text-[13px] text-gray-700 mb-1">Address</label>
                  <input
                    type="text"
                    name="address"
                    value={gst.address}
                    onChange={(e) => handleGstChange(idx, e)}
                    onBlur={(e) => handleGstBlur(idx, e)}
                    className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-blue-400 rounded-none"
                    required
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label className="block text-[13px] text-gray-700 mb-1">Pincode</label>
                  <input
                    type="text"
                    name="pincode"
                    value={gst.pincode}
                    onChange={(e) => handleGstChange(idx, e)}
                    onBlur={(e) => handleGstBlur(idx, e)}
                    className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-blue-400 rounded-none"
                    required
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label className="block text-[13px] text-gray-700 mb-1">PAN No</label>
                  <input
                    type="text"
                    name="pan_no"
                    value={gst.pan_no}
                    onChange={(e) => handleGstChange(idx, e)}
                    onBlur={(e) => handleGstBlur(idx, e)}
                    className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-blue-400 rounded-none"
                    required
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label className="block text-[13px] text-gray-700 mb-1">CIN No</label>
                  <input
                    type="text"
                    name="cin_no"
                    value={gst.cin_no}
                    onChange={(e) => handleGstChange(idx, e)}
                    onBlur={(e) => handleGstBlur(idx, e)}
                    className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-blue-400 rounded-none"
                    required
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label className="block text-[13px] text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={gst.email}
                    onChange={(e) => handleGstChange(idx, e)}
                    onBlur={(e) => handleGstBlur(idx, e)}
                    className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-blue-400 rounded-none"
                    required
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label className="block text-[13px] text-gray-700 mb-1">Phone</label>
                  <input
                    type="text"
                    name="phone"
                    value={gst.phone}
                    onChange={(e) => handleGstChange(idx, e)}
                    onBlur={(e) => handleGstBlur(idx, e)}
                    className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-blue-400 rounded-none"
                    required
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label className="block text-[13px] text-gray-700 mb-1">Account No</label>
                  <input
                    type="text"
                    name="accountNo"
                    value={gst.accountNo}
                    onChange={(e) => handleGstChange(idx, e)}
                    className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-blue-400 rounded-none"
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label className="block text-[13px] text-gray-700 mb-1">IFSC Code</label>
                  <input
                    type="text"
                    name="ifscCode"
                    value={gst.ifscCode}
                    onChange={(e) => handleGstChange(idx, e)}
                    className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-blue-400 rounded-none"
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label className="block text-[13px] text-gray-700 mb-1">Bank Name</label>
                  <input
                    type="text"
                    name="bankName"
                    value={gst.bankName}
                    onChange={(e) => handleGstChange(idx, e)}
                    className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-blue-400 rounded-none"
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label className="block text-[13px] text-gray-700 mb-1">Bank Address</label>
                  <input
                    type="text"
                    name="bankAddress"
                    value={gst.bankAddress}
                    onChange={(e) => handleGstChange(idx, e)}
                    className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-blue-400 rounded-none"
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label className="block text-[13px] text-gray-700 mb-1">Account Holder Name</label>
                  <input
                    type="text"
                    name="accountHolderName"
                    value={gst.accountHolderName}
                    onChange={(e) => handleGstChange(idx, e)}
                    className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-blue-400 rounded-none"
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label className="block text-[13px] text-gray-700 mb-1">Branch</label>
                  <input
                    type="text"
                    name="branch"
                    value={gst.branch}
                    onChange={(e) => handleGstChange(idx, e)}
                    className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-blue-400 rounded-none"
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label className="block text-[13px] text-gray-700 mb-1">Bank State</label>
                  <input
                    type="text"
                    name="bankstate"
                    value={gst.bankstate}
                    onChange={(e) => handleGstChange(idx, e)}
                    className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-blue-400 rounded-none"
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label className="block text-[13px] text-gray-700 mb-1">Branch Name</label>
                  <input
                    type="text"
                    name="branchName"
                    value={gst.branchName}
                    onChange={(e) => handleGstChange(idx, e)}
                    className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-blue-400 rounded-none"
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label className="block text-[13px] text-gray-700 mb-1">Branch Code</label>
                  <input
                    type="text"
                    name="branchCode"
                    value={gst.branchCode}
                    onChange={(e) => handleGstChange(idx, e)}
                    className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-blue-400 rounded-none"
                    autoComplete="off"
                  />
                </div>
              </div>
            </div>
          ))}
          <div className="flex justify-end mt-2">
            <button
              type="button"
              onClick={handleAddGst}
              className="text-[13px] px-3 py-1 rounded border font-medium bg-[#15803d] text-white border-[#15803d] hover:bg-[#166534]"
            >
              + Add GST Detail
            </button>
          </div>
        </div>
        <div className="pt-2 flex items-center gap-3">
          <button
            type="submit"
            className="px-3 py-1.5 text-[12px] text-[13px] bg-[#15803d] text-white font-normal rounded border border-[#15803d] hover:bg-[#166534] transition disabled:opacity-60"
            style={{ minWidth: '120px' }}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Add GST Details'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CompanyAddGstForm; 